import React, { FC } from 'react';
import { Divider, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PaperCard } from 'components/cards/paper-card';
import { TFunction } from 'i18next';
import { AccountingType } from 'v2-types/order';
import { getAccountingEntryByLedgerCode } from 'utils/accounting';
import {
  LEDGER_CODE_LOAN_AMOUNT,
  LEDGER_CODE_LOAN_NETFUNDED,
  CHARGES_LENDER_CREDITS,
  LEDGER_CODE_PAIDBY_OTHERS,
  LEDGER_CODE_LENDER_CHARGES,
  LEDGER_CODE_LOAN_FUNDING,
  LEDGER_CODE_AGGREGATE_ADJUSTMENTS
} from 'utils/constants';

import { BalancingItem } from './balancing-item';

const useStyles = makeStyles((theme: any) => ({
  divider: { backgroundColor: theme.palette.tab.offselected, opacity: 0.5 },
}));

type SettlementLoanFundingProps = {
  accounting: AccountingType[];
  t: TFunction;
};

export const SettlementLoanFunding: FC<SettlementLoanFundingProps> = ({
  accounting,
  t,
}) => {
  const classes = useStyles();

  const getEntryByLedgerCode = (ledgerCode: string) =>
    getAccountingEntryByLedgerCode({ accounting, ledgerCode });

  const loanAmount = getEntryByLedgerCode(LEDGER_CODE_LOAN_AMOUNT)?.amount || 0;
  const netFunded = getEntryByLedgerCode(LEDGER_CODE_LOAN_NETFUNDED)?.amount || 0;
  const lenderCredits = getEntryByLedgerCode(CHARGES_LENDER_CREDITS)?.amount || 0;
  const paidByOthers = getEntryByLedgerCode(LEDGER_CODE_PAIDBY_OTHERS)?.amount || 0;
  const lenderCharges = getEntryByLedgerCode(LEDGER_CODE_LENDER_CHARGES)?.amount || 0;
  const loanFunding = getEntryByLedgerCode(LEDGER_CODE_LOAN_FUNDING)?.amount || 0;
  const aggregateAdjustment = getEntryByLedgerCode(LEDGER_CODE_AGGREGATE_ADJUSTMENTS)?.amount || 0;

  return (
    <PaperCard title={t('orders:loanFundingTitle')}>
      <Grid container direction="column" spacing={1}>
        <Grid item>
          <BalancingItem title={t('orders:loanAmountTitle')} value={loanAmount} />
        </Grid>
        <Grid item>
          <BalancingItem title={t('orders:netFundedTitle')} value={netFunded} isNegativeIncome />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('orders:lenderCreditsTitle')}
            value={Math.abs(lenderCredits)}
            isNegativeIncome={lenderCredits > 0}
            isPositiveIncome={lenderCredits <= 0}
          />
        </Grid>
        <Grid item>
          <BalancingItem title={t('orders:paidByOthersTitle')} value={paidByOthers} isPositiveIncome />
        </Grid>
        <Grid item>
          <BalancingItem title={t('orders:lenderChargesTitle')} value={lenderCharges} isPositiveIncome />
        </Grid>
        <Grid item>
          <BalancingItem
            title={t('orders:aggregateAdjustmentTitle')}
            value={aggregateAdjustment}
            isPositiveIncome={aggregateAdjustment >= 0}
          />
        </Grid>
        <Grid item>
          <Divider className={classes.divider} />
        </Grid>
        <Grid item>
          <BalancingItem title={t('orders:loanFundingTitle')} value={loanFunding} />
        </Grid>
      </Grid>
    </PaperCard>
  );
};
